/**
 * The settlement, before anybody commits to it.
 *
 * Read-only and lock-free on the server, so HR reading this screen blocks nobody — it
 * *simulates* releasing the proposal's reservation rather than performing it.
 *
 * Two things are deliberate about the layout:
 *
 *   - **The excess is never hidden.** It is money the department or the organiser bears (R-29),
 *     and discovering it at approval time is the failure this whole screen exists to prevent.
 *   - **The proposal's estimate sits below the line**, labelled as reference. It is not a
 *     ceiling on the claim (D-43), and putting it above the line would imply it was.
 */
import { Alert, Card, Spinner } from '@/components/ui';
import { Money } from '@/components/bits';
import type { Settlement } from '@/lib/types';

export function SettlementPanel({
  settlement,
  pending,
  error,
  attendeeCount,
  contributingCount,
}: {
  settlement: Settlement | undefined;
  pending: boolean;
  error: unknown;
  attendeeCount?: number;
  contributingCount?: number;
}) {
  if (error) {
    const message = error instanceof Error ? error.message : String(error);
    return (
      <Card title="Settlement">
        <p className="text-destructive text-sm">{message}</p>
      </Card>
    );
  }

  if (!settlement) {
    return (
      <Card title="Settlement">
        <p className="text-muted-foreground flex items-center gap-2 text-sm">
          {pending ? <Spinner /> : null}
          {pending ? 'Working out the arithmetic…' : 'Record the cost and who came.'}
        </p>
      </Card>
    );
  }

  const short = settlement.excess_cents > 0;

  return (
    <Card
      title="Settlement"
      action={pending ? <Spinner /> : null}
      footer={
        <div className="flex items-baseline justify-between gap-4 text-sm">
          <span className="text-muted-foreground">Proposal estimate, for reference</span>
          <span className="text-right">
            <Money cents={settlement.proposal.approved_amount_cents} muted />
            {settlement.proposal.ref_no ? (
              <span className="text-muted-foreground ml-2 text-xs">
                {settlement.proposal.ref_no}
              </span>
            ) : null}
          </span>
        </div>
      }
    >
      <dl className="grid gap-2 text-sm">
        <Row label="Total cost">
          <Money cents={settlement.total_cost_cents} />
        </Row>
        <Row
          label={
            attendeeCount === undefined
              ? "Attendees' available"
              : `Attendees' available (${contributingCount ?? '—'} of ${attendeeCount})`
          }
        >
          <Money cents={settlement.pool_actual_cents} />
        </Row>
        {settlement.senior_topup_cents > 0 ? (
          <Row label="Senior-inclusion top-up (R-30)">
            <Money cents={settlement.senior_topup_cents} />
          </Row>
        ) : null}
      </dl>

      <div className="border-border mt-3 border-t pt-3">
        <dl className="grid gap-2 text-sm">
          <Row label="Reimbursable" strong>
            <Money cents={settlement.reimbursable_cents} />
          </Row>
          <Row label="Excess — not reimbursed">
            <Money cents={settlement.excess_cents} />
          </Row>
        </dl>
        {short ? (
          <p className="text-muted-foreground mt-1 text-xs">
            Borne by the department or the organiser (R-29). Nobody should discover this at approval
            time, which is why it is here.
          </p>
        ) : null}
      </div>

      {settlement.outstanding_hold_cents > 0 ? (
        <p className="text-muted-foreground mt-3 text-xs">
          Approving this releases <Money cents={settlement.outstanding_hold_cents} muted /> of
          reservation and writes the actual usage, in one transaction.
        </p>
      ) : null}

      {settlement.gate_crashers.length > 0 ? (
        <Alert
          tone="warn"
          title={`${settlement.gate_crashers.length} not on the namelist`}
          className="mt-3"
        >
          They bring no entitlement (R-26), so their share of the cost falls into the excess. A
          senior guest added only now is in the same position — for their own entitlement to count
          they have to be on the confirmed namelist (D-48).
        </Alert>
      ) : null}

      {settlement.flags.length > 0 ? (
        <ul className="mt-3 grid gap-1.5">
          {settlement.flags.map((flag) => (
            <li key={flag.code} className="text-muted-foreground text-xs">
              <span className="font-medium">{flag.rule_id}</span> — {flag.message}
            </li>
          ))}
        </ul>
      ) : null}
    </Card>
  );
}

function Row({
  label,
  children,
  strong = false,
}: {
  label: React.ReactNode;
  children: React.ReactNode;
  strong?: boolean;
}) {
  return (
    <div className="flex items-baseline justify-between gap-4">
      <dt className={strong ? 'font-medium' : 'text-muted-foreground'}>{label}</dt>
      <dd className={strong ? 'font-semibold' : ''}>{children}</dd>
    </div>
  );
}

/** The evidence links, once they exist — HR opens both, so they are real hyperlinks. */
export function EvidenceLinks({
  driveUrl,
  archiveUrl,
}: {
  driveUrl: string | null;
  archiveUrl: string | null;
}) {
  return (
    <dl className="grid gap-2 text-sm">
      <div className="flex flex-wrap items-baseline justify-between gap-2">
        <dt className="text-muted-foreground">Submitter&rsquo;s folder</dt>
        <dd>
          {driveUrl ? (
            <a
              href={driveUrl}
              target="_blank"
              rel="noreferrer"
              className="break-all underline underline-offset-4"
            >
              {driveUrl}
            </a>
          ) : (
            <span className="text-muted-foreground">not provided</span>
          )}
        </dd>
      </div>
      <div className="flex flex-wrap items-baseline justify-between gap-2">
        <dt className="text-muted-foreground">Org archive (D-22)</dt>
        <dd>
          {archiveUrl ? (
            <a
              href={archiveUrl}
              target="_blank"
              rel="noreferrer"
              className="break-all underline underline-offset-4"
            >
              {archiveUrl}
            </a>
          ) : (
            <span className="text-muted-foreground">not archived yet</span>
          )}
        </dd>
      </div>
    </dl>
  );
}

/**
 * The settlement as **recorded**, for a claim that has already been settled.
 *
 * Not the preview. `GET /claims/:id/settlement` re-simulates the arithmetic against today's
 * ledger, and after settlement the reservation is gone and the usage is written — so the
 * simulation correctly returns S$0 reimbursable, which on screen reads as "this claim paid
 * nothing". Verified in the browser: a claim settled at S$705 showed S$0 until this split
 * existed.
 *
 * A settled claim has one true set of figures and they are on the claim row. Use them.
 */
export function SettledPanel({
  claim,
}: {
  claim: {
    settlement: {
      pool_actual_cents: number | null;
      senior_topup_cents: number | null;
      reimbursable_cents: number | null;
      excess_cents: number | null;
      settled_at: string | null;
    };
    total_cost_cents: number;
    proposal: { ref_no: string | null; approved_amount_cents: number | null };
  };
}) {
  return (
    <Card
      title="Settled"
      footer={
        <div className="flex items-baseline justify-between gap-4 text-sm">
          <span className="text-muted-foreground">Proposal estimate, for reference</span>
          <span className="text-right">
            <Money cents={claim.proposal.approved_amount_cents} muted />
            {claim.proposal.ref_no ? (
              <span className="text-muted-foreground ml-2 text-xs">{claim.proposal.ref_no}</span>
            ) : null}
          </span>
        </div>
      }
    >
      <dl className="grid gap-2 text-sm">
        <Row label="Total cost">
          <Money cents={claim.total_cost_cents} />
        </Row>
        <Row label="Attendees' entitlement used">
          <Money cents={claim.settlement.pool_actual_cents} />
        </Row>
        {(claim.settlement.senior_topup_cents ?? 0) > 0 ? (
          <Row label="Senior-inclusion top-up (R-30)">
            <Money cents={claim.settlement.senior_topup_cents} />
          </Row>
        ) : null}
      </dl>
      <div className="border-border mt-3 border-t pt-3">
        <dl className="grid gap-2 text-sm">
          <Row label="Reimbursable" strong>
            <Money cents={claim.settlement.reimbursable_cents} />
          </Row>
          <Row label="Excess — not reimbursed">
            <Money cents={claim.settlement.excess_cents} />
          </Row>
        </dl>
      </div>
      <p className="text-muted-foreground mt-3 text-xs">
        Recorded when the HR Director approved it. These are the figures written to the ledger, not
        a recalculation — the entitlement they drew on has since been spent.
      </p>
    </Card>
  );
}
